import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "./AppSidebar";
import { API } from "./cache";
import "./TodoView.css";
import TagInputPanel from "./features/common/components/TagInputPanel";
import {
  listTags,
  createTag,
  createTagMapping,
  deleteTagMapping,
  listTagMappings,
} from "./features/common/services/tagsApi";

// Module-level ref so drag ID is never lost to async React state updates
let _dragItemId = null;

async function apiFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "include", ...opts });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Error ${res.status}`); }
  if (res.status === 204) return null;
  return res.json();
}

// ── Constants ─────────────────────────────────────────────────────────────────
const CATS = [
  { k: "Open",        col: "open",     title: "Open",        sub: "To do, reminder pending" },
  { k: "In Progress", col: "prog",     title: "In Progress", sub: "Actively working" },
  { k: "Completed",   col: "done",     title: "Completed",   sub: "Done — kept for 30 days" },
  { k: "Archived",    col: "archived", title: "Archived",    sub: "Hidden from normal view" },
];

const MONTHS_S  = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_U  = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
const DOW_U     = ["SUN","MON","TUE","WED","THU","FRI","SAT"];

// ── Helpers ───────────────────────────────────────────────────────────────────
function isoToday() { return new Date().toISOString().slice(0, 10); }

function addDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function daysDiff(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(dateStr + "T00:00:00") - today) / 86400000);
}

function initials(name) {
  if (!name || name === "me") return "ME";
  const p = name.trim().split(/\s+/);
  return (p[0][0] + (p[1] ? p[1][0] : "")).toUpperCase();
}

function fmtAge(isoStr) {
  if (!isoStr) return "";
  const d   = new Date(isoStr);
  const now = new Date();
  const diffMs   = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffDays = Math.floor(diffMins / 1440);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (diffMins < 1)  return "just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffDays === 0) return `today, ${hh}:${mm}`;
  if (diffDays === 1) return `yesterday, ${hh}:${mm}`;
  if (diffDays < 7)  return `${diffDays} days ago`;
  return `${d.getDate()} ${MONTHS_S[d.getMonth()]}`;
}

// Map an API row to the local item shape the UI expects
function apiToItem(row, myId) {
  return {
    id:           row.id,
    cat:          row.status,
    desc:         row.description,
    date:         row.due_date ? row.due_date.slice(0, 10) : null,
    time:         row.due_time || null,
    assignee:     row.assigned_to === myId ? "me" : (row.assigned_to_name || row.assigned_to),
    assignee_id:  row.assigned_to,
    createdBy:    row.created_by  === myId ? "me" : (row.created_by_name  || row.created_by),
    created_by_id:row.created_by,
    created:      fmtAge(row.created_at),
    tags:         Array.isArray(row.tags) ? row.tags.map(t => ({ id: t.mapping_id, label: t.tag_name })) : [],
  };
}

// ── WhenChip ──────────────────────────────────────────────────────────────────
function WhenChip({ date, time, cat }) {
  if (!date) return <span className="tdv-when none">· no date ·</span>;
  const diff = daysDiff(date);
  const d    = new Date(date + "T00:00:00");
  let label;
  if (diff === 0)       label = "TODAY";
  else if (diff === 1)  label = "TOMORROW";
  else if (diff === -1) label = "YESTERDAY";
  else if (diff > 1 && diff <= 6) label = DOW_U[d.getDay()];
  else label = `${d.getDate()} ${MONTHS_U[d.getMonth()]}`;

  let cls = "";
  if (cat !== "Completed" && cat !== "Archived") {
    if (diff < 0)       cls = "overdue";
    else if (diff === 0) cls = "today";
    else if (diff <= 2)  cls = "soon";
  }
  return (
    <span className={`tdv-when ${cls}`}>
      <span className="d">{label}</span>
      {time && <span className="t">· {time}</span>}
    </span>
  );
}

// ── DescText ──────────────────────────────────────────────────────────────────
function DescText({ text }) {
  const parts = text.split(/(#[A-Z][A-Z0-9-]*)/g);
  return (
    <div className="tdv-desc">
      {parts.map((p, i) =>
        p.startsWith("#") ? <span key={i} className="hash">{p}</span> : p
      )}
    </div>
  );
}

// ── Byline ────────────────────────────────────────────────────────────────────
function Byline({ item }) {
  if (item.assignee === "me" && item.createdBy === "me") return null;
  if (item.assignee === "me") {
    return (
      <span className="tdv-byline in">
        <span className="av">{initials(item.createdBy)}</span>
        from <b>{item.createdBy}</b>
      </span>
    );
  }
  if (item.createdBy === "me") {
    return (
      <span className="tdv-byline out">
        <span className="av">{initials(item.assignee)}</span>
        for <b>{item.assignee}</b>
      </span>
    );
  }
  return null;
}

// ── TodoCard ──────────────────────────────────────────────────────────────────
function TodoCard({ item, flash, isDragging, onEdit, onMove, onDelete, onDragStart, onDragEnd }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  return (
    <article
      className={`tdv-card${flash ? " flash" : ""}${isDragging ? " dragging" : ""}`}
      draggable
      onDragStart={(e) => {
        _dragItemId = item.id;
        e.dataTransfer.setData("text/plain", item.id);
        e.dataTransfer.effectAllowed = "move";
        // Defer so the browser snapshots the normal card as the ghost image
        // before React re-renders it with the .dragging class
        setTimeout(() => onDragStart(item.id), 0);
      }}
      onDragEnd={() => { _dragItemId = null; onDragEnd(); }}
      onClick={(e) => {
        if (!e.target.closest(".tdv-card-menu")) onEdit(item);
      }}
    >
      <div className="tdv-card-head">
        <WhenChip date={item.date} time={item.time} cat={item.cat} />
        <div
          className="tdv-card-menu"
          ref={menuRef}
          onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <circle cx="5" cy="12" r="1.2"/>
            <circle cx="12" cy="12" r="1.2"/>
            <circle cx="19" cy="12" r="1.2"/>
          </svg>
          {menuOpen && (
            <div className="tdv-pop" onClick={(e) => e.stopPropagation()}>
              <div className="sep">Move to</div>
              {CATS.filter(c => c.k !== item.cat).map(c => (
                <button
                  key={c.k}
                  type="button"
                  onClick={() => { onMove(item.id, c.k); setMenuOpen(false); }}
                >
                  <span className={`tdv-sw-inline ${c.col}`} />
                  {c.title}
                </button>
              ))}
              <div className="sep">Actions</div>
              <button type="button" onClick={() => { onEdit(item); setMenuOpen(false); }}>✎ Edit</button>
              <button type="button" className="danger" onClick={() => { onDelete(item.id); setMenuOpen(false); }}>✕ Delete</button>
            </div>
          )}
        </div>
      </div>
      <DescText text={item.desc} />
      <div className="tdv-card-foot">
        <Byline item={item} />
        {item.tags?.length > 0 && (
          <div className="tdv-card-tags">
            {item.tags.slice(0, 2).map(t => (
              <span key={t.id} className="tdv-ctag">{t.label}</span>
            ))}
            {item.tags.length > 2 && (
              <span className="tdv-ctag tdv-ctag--more">+{item.tags.length - 2}</span>
            )}
          </div>
        )}
        <span className="age">Created <b>{item.created}</b></span>
      </div>
    </article>
  );
}

// ── UserSearchSelect ──────────────────────────────────────────────────────────
function UserSearchSelect({ options, value, onChange, hasError }) {
  // value: user id string; onChange: (opt | null) => void
  const [query,  setQuery]  = useState("");
  const [open,   setOpen]   = useState(false);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.id === value) || null;
  const displayValue = open ? query : (selected ? selected.label + (selected.sublabel ? ` · ${selected.sublabel}` : "") : "");

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQuery(""); }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = (q
    ? options.filter(o =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel || "").toLowerCase().includes(q) ||
        (o.description || "").toLowerCase().includes(q)
      )
    : options
  ).slice(0, 60);

  return (
    <div className={`tdv-ss${open ? " open" : ""}${hasError ? " err" : ""}`} ref={wrapRef}>
      <div className="tdv-ss-wrap">
        <input
          ref={inputRef}
          type="text"
          className="tdv-ss-input"
          value={displayValue}
          placeholder="Search assignee…"
          onFocus={() => { setOpen(true); setQuery(""); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(null); }}
        />
        {selected && !open ? (
          <button className="tdv-ss-clear" onMouseDown={e => e.preventDefault()}
            onClick={() => { onChange(null); setQuery(""); inputRef.current?.focus(); }} tabIndex={-1}>×</button>
        ) : (
          <svg className="tdv-ss-chev" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        )}
      </div>
      {open && (
        <div className="tdv-ss-drop">
          <div className="tdv-ss-list">
            {filtered.length === 0
              ? <div className="tdv-ss-empty">No matches</div>
              : filtered.map(opt => (
                <div
                  key={opt.id}
                  className={`tdv-ss-opt${opt.id === value ? " on" : ""}`}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { onChange(opt); setOpen(false); setQuery(""); }}
                >
                  <div className="tdv-ss-av">{(opt.label || "?")[0].toUpperCase()}</div>
                  <div className="tdv-ss-text">
                    <span className="tdv-ss-lbl">{opt.label}</span>
                    {opt.description && <span className="tdv-ss-sub">{opt.description}</span>}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ── TodoSheet ─────────────────────────────────────────────────────────────────
function TodoSheet({ open, editItem, prefillCat, onClose, onSave, user }) {
  const [desc,      setDesc]      = useState("");
  const [forWho,    setForWho]    = useState("self");
  const [assignee,  setAssignee]  = useState(null);
  const [date,      setDate]      = useState("");
  const [time,      setTime]      = useState("");
  const [cat,       setCat]       = useState("Open");
  const [descErr,   setDescErr]   = useState(false);
  const [asnErr,    setAsnErr]    = useState(false);
  const [users,     setUsers]     = useState([]);
  const [saving,    setSaving]    = useState(false);
  const descRef = useRef(null);

  // ── Tag state ────────────────────────────────────────────────────────────────
  // liveTags: edit mode — [{mappingId, tagId, label}]; id in TagInputPanel = mappingId
  // draftTags: create mode — [{tagId, label}]; id in TagInputPanel = tagId
  const [tagCatalog,     setTagCatalog]     = useState([]);
  const [liveTags,       setLiveTags]       = useState([]);
  const [draftTags,      setDraftTags]      = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagQuery,       setTagQuery]       = useState("");
  const [tagsBusy,       setTagsBusy]       = useState(false);

  // Fetch user list once
  useEffect(() => {
    apiFetch(`${API}/users/dropdown`)
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.users || []);
        setUsers(list.map(u => ({
          id:          u.id,
          label:       u.display_name || u.name || u.user_name || "Unnamed",
          sublabel:    u.user_name || "",
          description: u.role || "",
        })));
      })
      .catch(() => {});
  }, []);

  // Load tag catalog (all todos-scoped tags) once on open
  useEffect(() => {
    if (!open) return;
    listTags()
      .then(rows => setTagCatalog(Array.isArray(rows) ? rows : []))
      .catch(() => setTagCatalog([]));
  }, [open]);

  // Load live tag mappings when editing an existing todo
  const reloadLiveTags = useCallback(async () => {
    if (!editItem?.id) return;
    try {
      const rows = await listTagMappings({ moduleName: "todos", entityId: editItem.id });
      setLiveTags(
        (Array.isArray(rows) ? rows : []).map(r => ({
          mappingId: r.id,
          tagId:     String(r.tag_id),
          label:     r.tag_name,
        }))
      );
    } catch { setLiveTags([]); }
  }, [editItem?.id]);

  // Typeahead: fetch suggestions as user types, excluding already-added tags
  useEffect(() => {
    const q = tagQuery.trim();
    if (!q) { setTagSuggestions([]); return; }
    let active = true;
    const addedNames = new Set(
      (editItem ? liveTags : draftTags).map(t => t.label.toLowerCase())
    );
    listTags({ search: q })
      .then(rows => {
        if (!active) return;
        setTagSuggestions(
          (Array.isArray(rows) ? rows : [])
            .filter(t => !addedNames.has(t.tag_name.toLowerCase()))
            .slice(0, 8)
            .map(t => ({ id: t.id, label: t.tag_name }))
        );
      })
      .catch(() => { if (active) setTagSuggestions([]); });
    return () => { active = false; };
  }, [tagQuery, liveTags, draftTags, editItem]);

  // Resolve or create a tag by name, returns { id, tag_name } or null
  const resolveOrCreateTag = async (name) => {
    const norm = name.trim().toLowerCase();
    const existing = tagCatalog.find(t => t.tag_name.toLowerCase() === norm);
    if (existing) return existing;
    try {
      const res = await createTag({ tag_name: name.trim() });
      const tag = res?.tag;
      if (tag?.id) { setTagCatalog(prev => [...prev, tag]); return tag; }
    } catch (e) {
      if (e?.status === 409) {
        const rows = await listTags({ search: name });
        return (Array.isArray(rows) ? rows : []).find(t => t.tag_name.toLowerCase() === norm) || null;
      }
    }
    return null;
  };

  const handleTagAdd = async (rawLabel) => {
    const name = String(rawLabel || "").trim();
    if (!name) return;
    setTagsBusy(true);
    try {
      const tag = await resolveOrCreateTag(name);
      if (!tag?.id) return;
      if (editItem?.id) {
        // Live mode: create mapping immediately
        const alreadyMapped = liveTags.some(t => t.tagId === String(tag.id));
        if (!alreadyMapped) {
          await createTagMapping({ module_name: "todos", entity_id: editItem.id, tag_id: tag.id });
        }
        await reloadLiveTags();
      } else {
        // Draft mode: add to local list; mapping created after todo save
        setDraftTags(prev =>
          prev.some(t => t.tagId === String(tag.id))
            ? prev
            : [...prev, { tagId: String(tag.id), label: tag.tag_name }]
        );
      }
      setTagQuery("");
      setTagSuggestions([]);
    } catch { /* silently ignore duplicate mapping */ }
    finally { setTagsBusy(false); }
  };

  const handleTagRemove = async (id) => {
    if (editItem?.id) {
      // id = mappingId
      setTagsBusy(true);
      try { await deleteTagMapping(id); await reloadLiveTags(); }
      catch { await reloadLiveTags(); }
      finally { setTagsBusy(false); }
    } else {
      // id = tagId
      setDraftTags(prev => prev.filter(t => t.tagId !== String(id)));
    }
  };

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setDesc(editItem.desc || "");
      setDate(editItem.date || "");
      setTime(editItem.time || "");
      setCat(editItem.cat || "Open");
      const isOther = editItem.assignee && editItem.assignee !== "me";
      setForWho(isOther ? "other" : "self");
      if (isOther) {
        const found = users.find(u =>
          u.id === editItem.assignee_id ||
          u.label.toLowerCase() === (editItem.assignee || "").toLowerCase()
        );
        setAssignee(found || { id: editItem.assignee_id || editItem.assignee, label: editItem.assignee, sublabel: "", description: "" });
      } else {
        setAssignee(null);
      }
      reloadLiveTags();
    } else {
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
      setDesc(""); setDate(""); setTime(currentTime); setCat(prefillCat || "Open");
      setForWho("self"); setAssignee(null);
      setDraftTags([]);
    }
    setTagQuery(""); setTagSuggestions([]);
    setDescErr(false); setAsnErr(false); setSaving(false);
    setTimeout(() => descRef.current?.focus(), 240);
  }, [open, editItem, prefillCat, users]);

  const tagPanelTags = editItem
    ? liveTags.map(t => ({ id: t.mappingId, label: t.label }))
    : draftTags.map(t => ({ id: t.tagId, label: t.label }));

  const handleSave = () => {
    if (!desc.trim()) { setDescErr(true); descRef.current?.focus(); return; }
    if (forWho === "other" && !assignee) { setAsnErr(true); return; }
    onSave({
      desc:         desc.trim(),
      date:         date || null,
      time:         time || null,
      cat,
      assignee:     forWho === "other" ? (assignee?.label || "me") : "me",
      assignee_id:  forWho === "other" ? (assignee?.id || null) : (user?.db_user_id || null),
      draftTagIds:  editItem ? null : draftTags.map(t => t.tagId),
    }, setSaving);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && open) onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && open) { e.preventDefault(); handleSave(); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  });

  const CHIPS = [
    { label: "Today",     val: isoToday() },
    { label: "Tomorrow",  val: addDays(1) },
    { label: "+2 days",   val: addDays(2) },
    { label: "Next week", val: addDays(7) },
    { label: "Clear",     val: "" },
  ];

  const isEdit = !!editItem;

  return (
    <>
      <div className={`tdv-scrim${open ? " show" : ""}`} onClick={onClose} />
      <aside className={`tdv-sheet${open ? " open" : ""}`} aria-hidden={!open}>
        <div className="tdv-sheet-head">
          <div>
            <h3>
              {isEdit
                ? <>Edit ToDo <em>— {editItem?.cat}</em></>
                : <>New ToDo <em>— quick reminder</em></>}
            </h3>
            <div className="sub">Press <b>⌘↵</b> to save · <b>Esc</b> to close</div>
          </div>
          <button className="tdv-sheet-close" onClick={onClose} aria-label="close">
            <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>

        <div className="tdv-sheet-body">

          {/* Status — edit only; new todos are always Open */}
          {isEdit && (
            <div className="tdv-field">
              <div className="fk">Status</div>
              <div className="tdv-cats">
                {CATS.map(c => (
                  <div key={c.k} className={`tdv-cat ${c.col}${cat === c.k ? " on" : ""}`} onClick={() => setCat(c.k)}>
                    <span className="sw" />
                    <span className="lbl">{c.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="tdv-field">
            <div className="fk">Reminder <span className="req">*</span></div>
            <textarea
              ref={descRef}
              className={`tdv-ta${descErr ? " err" : ""}`}
              placeholder="What do you want to remember?"
              value={desc}
              onChange={e => { setDesc(e.target.value); setDescErr(false); }}
            />
          </div>

          {/* Assignee */}
          <div className="tdv-field">
            <div className="fk">For <span className="opt">— default yourself</span></div>
            <div className="tdv-assignee">
              <div className={`tdv-who${forWho === "self" ? " on" : ""}`} onClick={() => setForWho("self")}>
                <span className="av">ME</span>
                <span className="lbl"><b>Myself</b><span>Private to you</span></span>
              </div>
              <div className={`tdv-who${forWho === "other" ? " on" : ""}`} onClick={() => setForWho("other")}>
                <span className="av plus">+</span>
                <span className="lbl"><b>Someone else</b><span>They'll see it too</span></span>
              </div>
            </div>
            {forWho === "other" && (
              <div style={{ marginTop: 10 }}>
                <UserSearchSelect
                  options={users}
                  value={assignee?.id || ""}
                  onChange={opt => { setAssignee(opt); setAsnErr(false); }}
                  hasError={asnErr}
                />
              </div>
            )}
          </div>

          {/* Date + Time */}
          <div className="tdv-row2">
            <div className="tdv-field" style={{ marginBottom: 0 }}>
              <div className="fk">When <span className="opt">— optional</span></div>
              <input className="tdv-in" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="tdv-field" style={{ marginBottom: 0 }}>
              <div className="fk">Time <span className="opt">— optional</span></div>
              <input className="tdv-in" type="time" value={time} onChange={e => setTime(e.target.value)} />
            </div>
          </div>

          {/* Quick-pick chips */}
          <div className="tdv-chips">
            {CHIPS.map(ch => (
              <button
                key={ch.label}
                className={`tdv-chip${date === ch.val && ch.val !== "" ? " on" : ""}`}
                onClick={() => setDate(ch.val)}
              >
                {ch.label}
              </button>
            ))}
          </div>

          {/* Tags */}
          <div className="tdv-field tdv-field--tags">
            <TagInputPanel
              title="Tags"
              tags={tagPanelTags}
              suggestions={tagSuggestions}
              onAdd={handleTagAdd}
              onRemove={handleTagRemove}
              onQueryChange={setTagQuery}
              placeholder="Add a tag and press Enter"
              busy={tagsBusy || saving}
            />
          </div>

        </div>

        <div className="tdv-sheet-foot">
          <div className="hint">
            {forWho === "other"
              ? <><b>They'll see this</b> in their ToDo · you stay creator</>
              : <>Stored <b>privately</b> · not shared</>}
          </div>
          <div className="acts">
            <button className="tdv-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="tdv-btn solid" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : (isEdit ? "Save changes" : "Save ToDo")}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function TodoView({ user }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("sb-collapsed") === "true");
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [viewMode,  setViewMode]  = useState("mine");   // mine | others | both
  const [filter,    setFilter]    = useState("all");    // all | today | overdue | none
  const [q,         setQ]         = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [prefillCat,setPrefillCat]= useState("Open");
  const [flashId,   setFlashId]   = useState(null);
  const [draggingId,  setDraggingId]  = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const myId = user?.db_user_id;

  const flash = (id) => { setFlashId(id); setTimeout(() => setFlashId(null), 1600); };

  // ── Load from API ────────────────────────────────────────────────────────────
  const loadTodos = useCallback(async () => {
    if (!myId) return;
    try {
      const rows = await apiFetch(`${API}/todos?view=all`);
      setItems((rows || []).map(r => apiToItem(r, myId)));
    } catch (err) {
      console.error("Failed to load todos:", err);
    } finally {
      setLoading(false);
    }
  }, [myId]);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  const today = isoToday();

  // ── View predicates ──────────────────────────────────────────────────────────
  const matchesView = useCallback((it) => {
    if (viewMode === "mine")   return it.assignee === "me";
    if (viewMode === "others") return it.createdBy === "me" && it.assignee !== "me";
    return true;
  }, [viewMode]);

  const isOverdue = (it) =>
    !!it.date && it.cat !== "Completed" && it.cat !== "Archived" && daysDiff(it.date) < 0;

  // ── Derived counts ───────────────────────────────────────────────────────────
  const viewSearched = useMemo(() => {
    let list = items.filter(matchesView);
    if (q) {
      const lq = q.toLowerCase();
      list = list.filter(i => (i.desc + " " + (i.date || "")).toLowerCase().includes(lq));
    }
    return list;
  }, [items, matchesView, q]);

  const filtered = useMemo(() => {
    if (filter === "today")   return viewSearched.filter(i => i.date === today && i.cat !== "Archived");
    if (filter === "overdue") return viewSearched.filter(i => isOverdue(i));
    if (filter === "none")    return viewSearched.filter(i => !i.date && i.cat !== "Archived");
    return viewSearched;
  }, [viewSearched, filter, today]);

  const ctAll     = viewSearched.length;
  const ctToday   = viewSearched.filter(i => i.date === today && i.cat !== "Archived").length;
  const ctOverdue = viewSearched.filter(i => isOverdue(i)).length;
  const ctNone    = viewSearched.filter(i => !i.date && i.cat !== "Archived").length;

  const vwMine   = items.filter(i => i.assignee === "me").length;
  const vwOthers = items.filter(i => i.createdBy === "me" && i.assignee !== "me").length;
  const vwBoth   = items.length;

  const scopeOpen   = items.filter(i => matchesView(i) && i.cat !== "Archived").length;
  const dueThisWeek = items.filter(i => {
    if (!matchesView(i) || !i.date || i.cat === "Completed" || i.cat === "Archived") return false;
    const d = daysDiff(i.date);
    return d >= 0 && d <= 6;
  }).length;

  // ── Actions ──────────────────────────────────────────────────────────────────
  const openNew  = (cat = "Open") => { setPrefillCat(cat); setEditItem(null); setSheetOpen(true); };
  const openEdit = (item) => { setEditItem(item); setSheetOpen(true); };

  const handleSave = async ({ desc, date, time, cat, assignee, assignee_id, draftTagIds }, setSaving) => {
    setSaving(true);
    try {
      if (editItem) {
        await apiFetch(`${API}/todos/${editItem.id}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            description: desc,
            status:      cat,
            due_date:    date  || null,
            due_time:    time  || null,
            assigned_to: assignee_id || null,
          }),
        });
        flash(editItem.id);
      } else {
        const created = await apiFetch(`${API}/todos`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            description: desc,
            due_date:    date       || null,
            due_time:    time       || null,
            assigned_to: assignee_id || null,
          }),
        });
        // Create tag mappings for any tags selected during draft
        if (created?.id && Array.isArray(draftTagIds) && draftTagIds.length > 0) {
          await Promise.allSettled(
            draftTagIds.map(tagId =>
              createTagMapping({ module_name: "todos", entity_id: created.id, tag_id: tagId })
            )
          );
        }
        if (assignee !== "me" && viewMode === "mine") setViewMode("others");
        flash(created?.id);
      }
      await loadTodos();
      setSheetOpen(false);
      setEditItem(null);
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (id, cat) => {
    flash(id);
    try {
      await apiFetch(`${API}/todos/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status: cat }),
      });
      await loadTodos();
    } catch (err) {
      console.error("Move failed:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`${API}/todos/${id}`, { method: "DELETE" });
      await loadTodos();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  // ── Keyboard shortcut ⌘N ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "n" && !sheetOpen) {
        const tag = document.activeElement?.tagName || "";
        if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") {
          e.preventDefault(); openNew();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [sheetOpen]);

  // ── Render ───────────────────────────────────────────────────────────────────
  const dateLabel = new Date().toLocaleDateString("en-GB", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  }).toUpperCase();

  return (
    <div
      className="tdv-root"
      style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}
    >
      <AppSidebar user={user} collapsed={sidebarCollapsed} onCollapse={() => setSidebarCollapsed(v => { const next = !v; localStorage.setItem("sb-collapsed", String(next)); return next; })} />

      <div className="tdv-shell">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="tdv-header">
          <div className="tdv-header-left">
            <h1>ToDo <em>— my later list</em></h1>
            <span className="tdv-status-pill">
              <span className="led" />
              live
            </span>
          </div>
          <div className="tdv-header-mid">
            <div className="tdv-scope">
              <span className="k">OPEN</span>
              <span className="v">{scopeOpen}</span>
              <span className="sb"><b>{dueThisWeek}</b> due this week</span>
            </div>
          </div>
          <div className="tdv-header-right">
            <button className="tdv-new-btn" onClick={() => openNew()}>
              <span className="plus">+</span> New ToDo
            </button>
          </div>
        </div>

        {/* ── View toggle ────────────────────────────────────────────────────── */}
        <div className="tdv-view">
          <button className={`tdv-vw${viewMode === "mine" ? " on" : ""}`} onClick={() => setViewMode("mine")}>
            <span className="avrow"><span className="av me">ME</span></span>
            <span className="lbl">Assigned to me <b>{String(vwMine).padStart(2,"0")}</b></span>
          </button>
          <button className={`tdv-vw${viewMode === "others" ? " on" : ""}`} onClick={() => setViewMode("others")}>
            <span className="avrow">
              <span className="av p1">+</span>
            </span>
            <span className="lbl">Created by me <b>{String(vwOthers).padStart(2,"0")}</b></span>
          </button>
          <button className={`tdv-vw${viewMode === "both" ? " on" : ""}`} onClick={() => setViewMode("both")}>
            <span className="lbl all">All <b>{String(vwBoth).padStart(2,"0")}</b></span>
          </button>
        </div>

        {/* ── Filter tabs ────────────────────────────────────────────────────── */}
        <div className="tdv-filters">
          {[
            { f: "all",     label: "All",      count: ctAll     },
            { f: "today",   label: "Due today", count: ctToday   },
            { f: "overdue", label: "Overdue",   count: ctOverdue },
            { f: "none",    label: "No date",   count: ctNone    },
          ].map(({ f, label, count }) => (
            <div key={f} className={`tdv-filter${filter === f ? " on" : ""}`} onClick={() => setFilter(f)}>
              {label} <span className="ct">{String(count).padStart(2,"0")}</span>
            </div>
          ))}
          <div className="tdv-filters-right">
            <label className="tdv-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="7"/><path d="M21 21l-5-5"/>
              </svg>
              <input placeholder="Find a reminder…" value={q} onChange={e => setQ(e.target.value)} />
            </label>
          </div>
        </div>

        {/* ── Meta bar ───────────────────────────────────────────────────────── */}
        <div className="tdv-meta">
          <div className="left">
            <span>
              <span className={`dot ${loading ? "amber" : "green"}`} />
              {loading ? "Loading…" : "Synced"}
            </span>
            <span><span className="k">SORT</span>Due date · ascending</span>
            <span className="tip"><span className="k">TIP</span>Click any card to edit · <b>⌘N</b> new</span>
          </div>
          <div className="right">{dateLabel}</div>
        </div>

        {/* ── Kanban board ───────────────────────────────────────────────────── */}
        <div className="tdv-board-wrap">
          <div className="tdv-board">
            {CATS.map(c => {
              const colCards      = filtered.filter(i => i.cat === c.k);
              const totalInCat    = items.filter(i => i.cat === c.k).length;
              const dragSourceCat = draggingId ? items.find(i => i.id === draggingId)?.cat : null;
              const isDropTarget  = dragOverCol === c.k && dragSourceCat && dragSourceCat !== c.k;
              return (
                <section
                  key={c.k}
                  className={`tdv-col ${c.col}${isDropTarget ? " drag-over" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCol(c.k); }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = _dragItemId || e.dataTransfer.getData("text/plain");
                    _dragItemId = null;
                    const src = items.find(i => i.id === id);
                    if (id && src && src.cat !== c.k) handleMove(id, c.k);
                    setDragOverCol(null);
                    setDraggingId(null);
                  }}
                >
                  <div className="tdv-col-head">
                    <span className="ttl"><span className="sw" />{c.title}</span>
                    <span className="ct">
                      <b>{String(colCards.length).padStart(2,"0")}</b>
                      {colCards.length !== totalInCat ? ` / ${totalInCat}` : ""}
                    </span>
                    <button className="add" title={`New ${c.title}`} onClick={() => openNew(c.k)}>+</button>
                  </div>
                  <div className="tdv-col-body">
                    {loading ? (
                      <div className="tdv-empty"><span className="big">Loading…</span></div>
                    ) : colCards.length === 0 ? (
                      <div className="tdv-empty">
                        <span className="big">
                          {c.k === "Open"        ? "Nothing waiting."  :
                           c.k === "In Progress" ? "Nothing in flight." :
                           c.k === "Completed"   ? "Nothing wrapped."  :
                                                   "Nothing archived."}
                        </span>
                        {c.k === "Open"     ? "click + to add a reminder" :
                         c.k === "Archived" ? "stale items move here"     :
                                              "move cards in or add new"}
                      </div>
                    ) : (
                      colCards.map(item => (
                        <TodoCard
                          key={item.id}
                          item={item}
                          flash={flashId === item.id}
                          isDragging={draggingId === item.id}
                          onEdit={openEdit}
                          onMove={handleMove}
                          onDelete={handleDelete}
                          onDragStart={setDraggingId}
                          onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                        />
                      ))
                    )}
                    {c.k === "Open" && colCards.length > 0 && (
                      <button className="tdv-quickadd" onClick={() => openNew("Open")}>
                        <span className="plus">+</span> Quick add
                      </button>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>

      </div>

      <TodoSheet
        open={sheetOpen}
        editItem={editItem}
        prefillCat={prefillCat}
        onClose={() => { setSheetOpen(false); setEditItem(null); }}
        onSave={handleSave}
        user={user}
      />
    </div>
  );
}
